import { EFileBadgeStatus, ETicketStatus, ETicketStatusGroup } from '../../../../types/enums'
import { TAmountChangeSource } from '../../../../components/AmountChangeBadge'
import { Currency, ErrorObject} from '../../settings/core/_models'
import { CashBoxAttempt } from '../../ticket-card/core/cashBoxAttempts.types'
import { MatchingFile } from '../../matching/core/_models'

export interface PaymentTicketWithTransactions {
  ticketToken: string
  merchantTransactionId: string
  paymentMethod: string
  createdAt: string
  completionDate?: string
  userId: number
  externalUserId: string
  // Токен клиента для ссылки на карточку /client/info/{token}; у старых ответов API его нет.
  clientToken?: string | null
  amount: number
  ticketStatus: string
  // Почему заявка завершилась: статус говорит, насколько далеко она прошла, и один и тот же
  // EXPIRED прилетает и на отмену клиентом, и на истёкший срок. Приходит только у завершённых.
  closeReason?: string | null
  closeReasonTitle?: string | null
  // Reason the client picked when cancelling on the payment page; manager/admin only.
  clientCancelReasonTitle?: string | null
  transactions: Transaction[] | null
  sms: string | null
  author: string | null
  executor: string | null
  completionType: PaymentTicketProcessType
  merchant: string | null
  downloadLink: string | null
  autoCheckStatus: EFileBadgeStatus | null
  autoCheckMessage: string | null
  // Все чеки пополнения: текущий первым, затем заменённые сотрудником. У старых ответов API поля нет.
  files?: MatchingFile[]
  // Только ролям, которым виден мерчант; нужен, чтобы не предлагать замену чека виртуального мерчанта.
  merchantId?: number | null
  isFake: boolean
  // Выданный клиенту реквизит; приходит только оператору и старшему оператору.
  requisite?: FinishedTicketRequisite | null
}

export interface FinishedTicketRequisite {
  identifier: string
  bank?: string | null
  cardholder?: string | null
}

export interface Transaction {
  paymentId?: string
  createdAt: string
  completedAt?: string
  paymentAdapter: string
  cashbox?: string
  requisiteName?: string
  status: string
  error?: string
  errorRaw?: string
  // Cassa attempts of a local-adapter row; sent to staff who see cassas only, null on other adapters.
  cashBoxAttempts?: CashBoxAttempt[] | null
}

export interface TicketListObject {
  [token: string]: PaymentTicket
}

export interface ReportBlockItems {
  [token: string]: Array<ReportBlockItem>
}

export enum PaymentTicketProcessType {
  AUTO = 'AUTO',
  MANUAL = 'MANUAL',
  NOT_PROCESSED = 'NOT_PROCESSED',
}

export interface IssuedRequisite {
  requisiteType: string
  cardNumber: string
  phone?: string
  accountNumber?: string
  currency: Currency
  name: string
  createdAt: string
}

export interface Sms {
  id: number
  text: string
  receivingDate: string
  phoneFrom: string
  createdAt: string
}

export interface TicketMerchant {
  token: string
  title: string
  enabled: boolean
  createdAt: string
}

export interface TicketCashBox {
  id: number
  title: string
  createdAt: string
}

export interface TicketExecutor {
  id: number
  uuid: string
  enabled: boolean
  createdAt: string
}

export interface ExternalUser {
  id: number
  token: string
  externalId: string
  createdAt: string
}

export type TTicketSource = {
  name: 'MANUAL' | 'SMS' | 'MERCHANT'
  value: 'MANUAL' | 'SMS' | 'MERCHANT'
}

export interface ExternalTransaction {
  createdAt: string
  paymentAdapterName: string
  paymentAdapterType: string
  paymentId: string
  requisite: string
  bank: string | null
  adapterStatus?: string|null
}

export interface PaymentTicket {
  status: ETicketStatus
  issuedRequisite: IssuedRequisite
  sms: Sms | null
  requisiteType: string
  currency: Currency
  merchant: TicketMerchant
  paymentMethod: { type: string }
  completionDate: string
  amount: number
  bill: string
  notifiedDate: string | null
  comment: string | null
  token: string
  callbackUrl: string
  executor: TicketExecutor | null
  createdAt: string
  paymentCurrency?: string
  operationDate?: string
  cashBox?: TicketCashBox
  externalUser?: ExternalUser
  externalTransactions: ExternalTransaction[]
  lastExternalTransaction: ExternalTransaction
  processType: string
  unknownPaymentTicket: boolean
  source: TTicketSource
  transactionId: string
  externalUserApproximateAmount: number | string | null
  // CPAYMENT-1669: сумма к переводу и данные бейджа «было → стало».
  requisiteAmount?: number | null
  amountChanged?: boolean
  amountChangeSource?: TAmountChangeSource | null
  isFake: boolean
  /** Concierge deposit: the row offers the staff concierge window. */
  concierge?: boolean
}

/**
 * Одна причина закрытия внутри статуса.
 *
 * `code` и `title` пусты у заявок, закрытых до появления поля, — такая строка остаётся в разборе
 * отдельно, иначе итог карточки перестаёт сходиться. Незнакомый `code` приходит с непустым `title`,
 * равным самому коду: интерфейс отстал от бэкенда, и лучше показать код, чем промолчать.
 */
export interface OutcomeBreakdownRow {
  code: string | null
  title: string | null
  ticketsCount: number
  sum: number
}

export interface ReportBlockItem {
  status: 'ACTIVE' | 'IN_PROGRESS' | 'COMPLETED' | 'DECLINED' | 'EXPIRED'
  ticketsCount: number
  sum: number
  commission_sum: string
  /** Разбор статуса по причинам закрытия. У пополнений — по всем финальным статусам,
   *  у выплат — только по отменённым; где разбора нет, ключ не приходит вовсе. */
  breakdown?: Array<OutcomeBreakdownRow>
}

export interface TicketListResponse {
  success: boolean
  tickets: Array<PaymentTicket>
  totalRows: number
  errors: Partial<ErrorObject>
}

export interface TicketActionResponse {
  success: boolean
  ticket: PaymentTicket
  errors: Partial<ErrorObject>
  totalRows: number
}

export interface ReportTicketsResponse {
  success: boolean
  data: Array<ReportBlockItem>
  totalRows: number
  errors: Partial<ErrorObject>
}

export interface ParamsTicketsRequest {
  euConfirmed?: boolean
  ticketToken?: string
  status?: Array<ETicketStatus> | string
  /** Бизнес-статусы (группы) — только для мерчантских ролей (оператор/старший оператор) */
  statusGroup?: Array<ETicketStatusGroup>
  page: number
  perPage: number
  createdDateStart?: string
  createdDateEnd?: string
  merchantKey?: string
  cashBoxId?: number
  ticketProcessType?: string
  unknownPayment?: boolean
}

export function PrepareTicketsToObject(tickets: Array<PaymentTicket>): TicketListObject {
  let object: TicketListObject = {}

  tickets.forEach((item) => {
    object[item.token] = item
  })

  return object
}
